import { useEffect, useState } from 'react'
import { LoggerName, namedLogger } from '../../../../../utils/logger'

const logger = namedLogger(LoggerName.useHtmlVideoPlayerState)

interface HtmlVideoPlayerState {
  currentTimeInSeconds: number
  durationInSeconds: number
  isPlaying: boolean
  isBuffering: boolean
  speed: number
}

/**
 * While a source is loading (or mid-swap), video.duration reports NaN/Infinity. Fall back to the clip's
 * API-derived duration so the controls never flash 0:00 waiting for metadata; the element's real duration
 * returns once known
 */
const durationOrFallback = (video: HTMLVideoElement, fallbackDurationInSeconds: number): number =>
  Number.isFinite(video.duration) ? video.duration : fallbackDurationInSeconds

export const useHtmlVideoPlayerState = (video: HTMLVideoElement, fallbackDurationInSeconds = 0): HtmlVideoPlayerState => {
  const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(video.currentTime)
  const [durationInSeconds, setDurationInSeconds] = useState(durationOrFallback(video, fallbackDurationInSeconds))
  const [isPlaying, setIsPlaying] = useState(!video.paused)
  const [isBuffering, setIsBuffering] = useState(false)
  const [speed, setSpeed] = useState(video.playbackRate)

  useEffect(() => {
    const handleTimeUpdate = () => setCurrentTimeInSeconds(video.currentTime)
    const handleDurationChange = () => {
      logger.debug('useHtmlVideoPlayerState duration change', video.duration)
      return setDurationInSeconds(durationOrFallback(video, fallbackDurationInSeconds))
    }
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleWaiting = () => setIsBuffering(true)
    const handlePlaying = () => setIsBuffering(false)
    const handleCanPlay = () => setIsBuffering(false)
    const handleRateChange = () => setSpeed(video.playbackRate)

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('playing', handlePlaying)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('ratechange', handleRateChange)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('durationchange', handleDurationChange)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('playing', handlePlaying)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('ratechange', handleRateChange)
    }
  }, [video, fallbackDurationInSeconds])

  return { currentTimeInSeconds, durationInSeconds, isPlaying, isBuffering, speed }
}
